import { useState } from 'react';
import {
  Button,
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
  useGetList,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Theme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { ChipList } from '../common';

export const AddUserToGroupButton = () => {
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Styles
  const styles = {
    dialogContent: {
      px: { xs: 1, sm: 3 },
      py: 2,
      overflow: 'hidden',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      p: 3,
    },
    emptyStateContainer: {
      p: 2,
    },
    instructionText: {
      mb: 2,
      px: 1,
    },
    list: {
      bgcolor: 'background.paper',
      borderRadius: 1,
      maxHeight: '400px',
      overflow: 'auto',
    },
    listItem: {
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:last-child': { borderBottom: 'none' },
    },
    listItemButton: {
      py: { xs: 1.5, sm: 2 },
      px: { xs: 1.5, sm: 2.5 },
      '&.Mui-selected': {
        backgroundColor: (theme: Theme) =>
          theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.16)' : 'rgba(25, 118, 210, 0.12)',
        borderLeft: '4px solid',
        borderLeftColor: 'primary.main',
        '&:hover': {
          backgroundColor: (theme: Theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(144, 202, 249, 0.24)'
              : 'rgba(25, 118, 210, 0.18)',
        },
      },
    },
    userContainer: {
      flex: 1,
    },
    username: {
      mb: 0.5,
    },
    chipsContainer: {
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      gap: { xs: 0.5, sm: 1 },
    },
    dialogActions: {
      px: { xs: 2, sm: 3 },
      py: { xs: 1.5, sm: 2 },
      gap: 1,
    },
  };

  // Get all users that don't belong to this group
  const {
    data: users,
    isLoading,
    refetch,
  } = useGetList(
    'users',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'username', order: 'ASC' },
      filter: {},
    },
    { enabled: open }
  );

  // Get all groups to display group names
  const { data: groups } = useGetList(
    'groups',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
      filter: {},
    },
    { enabled: open }
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSelectedUserId(null);
  };

  const handleAddUser = async () => {
    if (!selectedUserId) {
      notify('Please select a user first', { type: 'warning' });
      return;
    }

    try {
      await dataProvider.update('users', {
        id: selectedUserId,
        data: { groupId: record?.id },
        previousData: {},
      });

      notify('User added to group successfully', { type: 'success' });
      refresh();
      // Reset selection and refresh the list
      setSelectedUserId(null);
      refetch();
    } catch {
      notify('Error adding user to group', { type: 'error' });
    }
  };

  if (!record) return null;

  // Filter users that are not in this group
  const availableUsers = users?.filter((user) => user.groupId !== record.id) || [];

  // Helper function to get group name by ID
  const getGroupName = (groupId: number | null) => {
    if (!groupId) return null;
    const group = groups?.find((g) => g.id === groupId);
    return group?.name || 'Unknown Group';
  };

  return (
    <>
      <Button label="Add User" onClick={handleOpen} startIcon={<GroupAddIcon />} />
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Typography variant={isMobile ? 'h6' : 'h5'} component="div">
            Add User to {record.name}
          </Typography>
        </DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          {isLoading ? (
            <Box sx={styles.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : availableUsers.length === 0 ? (
            <Box sx={styles.emptyStateContainer}>
              <Typography color="text.secondary" textAlign="center">
                No users available to add. All users are already in this group or other groups.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={styles.instructionText}>
                Select a user to add to this group:
              </Typography>
              <List sx={styles.list}>
                {availableUsers.map((user) => (
                  <ListItem key={user.id} disablePadding sx={styles.listItem}>
                    <ListItemButton
                      onClick={() => setSelectedUserId(user.id)}
                      selected={selectedUserId === user.id}
                      sx={styles.listItemButton}
                    >
                      <Box sx={styles.userContainer}>
                        <Typography
                          variant={isMobile ? 'body2' : 'body1'}
                          fontWeight={selectedUserId === user.id ? 600 : 400}
                          sx={styles.username}
                        >
                          {user.username}
                        </Typography>
                        <Box sx={styles.chipsContainer}>
                          <ChipList
                            items={[
                              {
                                id: 'role',
                                label: `Role: ${user.role}`,
                                color: 'primary',
                              },
                              user.groupId
                                ? {
                                    id: 'group',
                                    label: `Group: ${getGroupName(user.groupId)}`,
                                    color: 'secondary' as const,
                                  }
                                : {
                                    id: 'no-group',
                                    label: 'No group assigned',
                                    color: 'default' as const,
                                  },
                            ]}
                          />
                        </Box>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button
            label="Cancel"
            onClick={handleClose}
            variant="outlined"
            startIcon={<CloseIcon />}
          />
          <Button
            label="Add User"
            onClick={handleAddUser}
            variant="contained"
            disabled={!selectedUserId || isLoading}
            startIcon={<GroupAddIcon />}
            color="primary"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
